import React from 'react';
import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import useMileageAgreement, {MileageAgreementStatus} from '@di-shared/features/mileage/agreement/useMileageAgreement';
import {WebviewUtils} from '@shared/webviewUtils';
import {ROUTE_PATHS} from '@di-shared/routes';
import {useNavigate} from 'react-router';
import useMileageAgreementNotice from '@di-shared/features/mileage/agreement/useMileageAgreementNotice';
import useWindowFocusTracker from '@di-shared/features/common/useWindowFocusTracker';
import usePendingHandler from '@shared/usePendingHandler';
import {diApi} from '@di-shared/api/di';
import useApiError from '@di-shared/features/common/useApiError';
import {BrandType} from '@shared/cci/features/const/environment';
import {useSuspenseQueries} from '@tanstack/react-query';
import {getQueryKey} from '@shared/queryKeyMap';
import {SEOUL_MILEAGE_TERMS_KEY} from '@di-shared/features/mileage/agreement/const';
import useCcWebviewUtilsHelper from '@di-shared/features/common/useCcWebviewUtilsHelper';

const LINK_MILEAGE_SOEUL_JOIN = 'https://ecomileage.seoul.go.kr/user/join.do';
const LINK_MILEAGE_SOEUL_LOGIN = 'https://ecomileage.seoul.go.kr/user/login.do';
const LINK_MILEAGE_SOEUL_REGISTER_CAR = 'https://ecomileage.seoul.go.kr/itf/adt/car/add.do';

interface MileageAgreementProps {
  vin: string;
}

const RequestBrand = {
  [BrandType.HYUNDAI]: '01',
  [BrandType.KIA]: '02',
  [BrandType.GENESIS]: '03',
};

function MileageAgreement({vin}: MileageAgreementProps) {
  const {t} = useInferableTranslation();
  const brand = import.meta.env.VITE_BRAND;
  const navigate = useNavigate();
  const {resolveError} = useApiError();
  const {backOrClose} = useCcWebviewUtilsHelper();

  const [termsData, memberInfoData, checkLinkData] = useSuspenseQueries({
    queries: [
      {
        queryKey: [getQueryKey(diApi.termsController.getOptionalTerms1), vin],
        queryFn: () => diApi.termsController.getOptionalTerms1({termsKey: SEOUL_MILEAGE_TERMS_KEY}),
      },
      {
        queryKey: [getQueryKey(diApi.ecoMileageController.getMemberInfo), vin],
        queryFn: () => diApi.ecoMileageController.getMemberInfo({vin}),
      },
      {
        queryKey: [getQueryKey(diApi.ecoMileageController.checkLink), vin],
        queryFn: () => diApi.ecoMileageController.checkLink({vin}),
      },
    ],
  });
  const terms = termsData.data.data.optionalTermsList.map((term) => ({
    ...term,
    // 실제 체크박스가 선택되어야 동의할수 있음
    isRequired: true,
    // [필수] [선택] 표시여부
    isRequiredText: false,
  }));

  const {getMileageAgreementStatus} = useMileageAgreement({terms});

  const {
    openSeoulMileageSignUpInfoConfirm,
    openUnifiedMembershipTransitionConfirm,
    openUnsupportedServiceAlert,
    openVehicleRegistrationGuideConfirm,
    openAgreementSuccessAlert,
    openAgreementFailDueToSeoulAlert,
  } = useMileageAgreementNotice();

  const [signupMileage] = usePendingHandler(
    async () => {
      const {mber_yn: mberYn, confirm_yn: confirmYn} = memberInfoData.data.data.rtn_data;
      const {car_con_yn: carConYn, car_mber_yn: carMberYn} = checkLinkData.data.data.rtn_data;

      const mileageAgreementStatus = getMileageAgreementStatus({mberYn, confirmYn, carConYn, carMberYn});

      if (mileageAgreementStatus === MileageAgreementStatus.INTEGRATION_AVAILABLE) {
        await diApi.ecoMileageController.agree1({
          vin,
          requestBody: {
            vin,
            brand: RequestBrand[brand as keyof typeof RequestBrand],
          },
        });

        await openAgreementSuccessAlert();
        navigate(ROUTE_PATHS.MILEAGE_ECO.getFullPath(vin), {replace: true});
      } else {
        await checkAndAlertMileageInstructions(mileageAgreementStatus);
      }
    },
    async (error) => {
      //TODO: error 케이스마다 실패 모달이 다름
      await openAgreementFailDueToSeoulAlert();
      await resolveError(error);
    }
  );

  const {enableWindowFocusTracker, disableWindowFocusTracker} = useWindowFocusTracker({
    isInitiallyEnabled: false,
    onFocus: () => signupMileage(),
  });

  async function checkAndAlertMileageInstructions(mileageAgreementStatus: MileageAgreementStatus) {
    // 회원가입
    if (mileageAgreementStatus === MileageAgreementStatus.MEMBERSHIP_REQUIRED) {
      const selectedIndex = await openSeoulMileageSignUpInfoConfirm();
      disableWindowFocusTracker();
      if (selectedIndex === 0) {
        enableWindowFocusTracker();
        WebviewUtils.openWebBrowser(LINK_MILEAGE_SOEUL_JOIN);
      }
      if (selectedIndex === 1) {
        navigate(ROUTE_PATHS.HOME_BENEFIT_TAB.getFullPath(vin));
      }
    }
    if (mileageAgreementStatus === MileageAgreementStatus.INTEGRATED_MEMBERSHIP_CONVERSION_REQUIRED) {
      // 통합 회원
      const isConfirm = await openUnifiedMembershipTransitionConfirm();
      disableWindowFocusTracker();
      if (isConfirm) {
        enableWindowFocusTracker();
        WebviewUtils.openWebBrowser(LINK_MILEAGE_SOEUL_LOGIN);
      }
    }
    if (mileageAgreementStatus === MileageAgreementStatus.UNSUPPORTED_VEHICLE) {
      await openUnsupportedServiceAlert();
      navigate(ROUTE_PATHS.HOME.getFullPath(vin));
    }
    if (mileageAgreementStatus === MileageAgreementStatus.VEHICLE_REGISTRATION_REQUIRED) {
      // 차량 등록
      const isConfirm = await openVehicleRegistrationGuideConfirm();
      disableWindowFocusTracker();
      if (isConfirm) {
        enableWindowFocusTracker();
        WebviewUtils.openWebBrowser(LINK_MILEAGE_SOEUL_REGISTER_CAR);
      }
    }
  }

  return (
    <>
      <main
        id="content"
        className="content page_eco_mileage"
      >
        <section className="section section_mileage_consent">
          <div className="section_heading">
            <h2
              className="section_heading_title"
              style={{whiteSpace: 'pre-wrap'}}
            >
              {t('T30196')}
            </h2>
            <p
              className="section_heading_desc"
              style={{whiteSpace: 'pre-wrap'}}
            >
              {t('T30197')}
            </p>
          </div>

          <div className="consent_agree">
            <div className="divider"></div>
            {terms.map(({termsId, title}) => (
              <div key={termsId}>
                <div className="consent_agree_item">
                  <p className="consent_agree_desc">
                    {`[${t('T30309')}]`}&nbsp;{title}
                  </p>
                  <button
                    type="button"
                    className="btn btn_ic"
                    onClick={() => {
                      navigate(ROUTE_PATHS.MILEAGE_AGREEMENT_DETAIL.getFullPath(vin, termsId) + location.search, {replace: true});
                    }}
                  >
                    <span
                      className="ic ic_arrow_forward_16px"
                      aria-hidden="true"
                    >
                      <span className="blind">{t('T10063')}</span>
                    </span>
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>
      </main>

      <div className="floating_action_area">
        <div className="button_box_complex">
          <button
            type="button"
            className="btn btn_large btn_outline btn_full"
            onClick={() => backOrClose()}
          >
            {t('T20119')}
          </button>
          <button
            type="button"
            className="btn btn_large btn_filled btn_full"
            onClick={() => signupMileage()}
          >
            {t('T20120')}
          </button>
        </div>
      </div>
    </>
  );
}

export default MileageAgreement;

export function MileageAgreementLoading() {
  const {t} = useInferableTranslation();
  return (
    <>
      <main
        id="content"
        className="content page_eco_mileage"
      >
        <section className="section section_mileage_consent">
          <div className="section_heading">
            <h2
              className="section_heading_title"
              style={{whiteSpace: 'pre-wrap'}}
            >
              {t('T30196')}
            </h2>
            <p
              className="section_heading_desc"
              style={{whiteSpace: 'pre-wrap'}}
            >
              {t('T30197')}
            </p>
          </div>

          <div className="consent_agree skeleton">
            <div className="divider"></div>
            <div className="consent_agree_item">
              <p className="consent_agree_desc">{`[${t('T30309')}]`}</p>
              <button
                type="button"
                className="btn btn_ic"
              >
                <span
                  className="ic ic_arrow_forward_16px"
                  aria-hidden="true"
                >
                  <span className="blind">{t('T10063')}</span>
                </span>
              </button>
            </div>
          </div>
          <div className="consent_agree skeleton">
            <div className="divider"></div>
            <div className="consent_agree_item">
              <p className="consent_agree_desc">{`[${t('T30309')}]`}</p>
              <button
                type="button"
                className="btn btn_ic"
              >
                <span
                  className="ic ic_arrow_forward_16px"
                  aria-hidden="true"
                >
                  <span className="blind">{t('T10063')}</span>
                </span>
              </button>
            </div>
          </div>
        </section>
      </main>

      <div className="floating_action_area">
        <div className="button_box_complex">
          <button
            type="button"
            className="btn btn_large btn_outline btn_full"
          >
            {t('T20119')}
          </button>
          <button
            type="button"
            className="btn btn_large btn_filled btn_full"
          >
            {t('T20120')}
          </button>
        </div>
      </div>
    </>
  );
}
